import React, { useState } from "react";
import { motion } from "framer-motion";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Link } from "react-router-dom";

import { MarketingFooter } from "@/components/layout/MarketingFooter";

interface ChangelogEntry {
  date: string;
  version: string;
  title: string;
  description: string;
  details: string[];
  category: "feature" | "fix" | "enhancement";
  commit: string;
}

const changelogData: ChangelogEntry[] = [
  {
    date: "November 1, 2025",
    version: "2.8.0",
    title: "Chat with Candidates & Data Chunking",
    description:
      "Chat interface for recruiters to interact with candidate data based on RAG and intelligent chunking",
    details: [
      "Added intelligent data chunking so that recruiter can query across candidate's resume, spotlight intotros, and previous interview transcripts seamlessly",
      "Backfilled existing candidate data with new chunking strategy for improved search and retrieval",
      "Streamlined chunking pipeline for BYOR imports to handle large datasets efficiently",
      "Implemented real-time chat interface for recruiters to interact with candidates data and RAG based responses grounded in candidate information",
    ],
    category: "feature",
    commit: "0739a74",
  },
  {
    date: "October 30, 2025",
    version: "2.7.5",
    title: "Company Management UI & Bug Fixes",
    description: "Enhanced company profile management with UI improvements",
    details: [
      "Redesigned manage company page with improved layout and navigation",
      "Fixed critical bugs in company profile editing and validation",
      "Added better error handling and user feedback for company updates",
      "Improved form accessibility and keyboard navigation",
      "Enhanced responsive design for mobile company management",
    ],
    category: "enhancement",
    commit: "523d266",
  },
  {
    date: "October 28, 2025",
    version: "2.7.0",
    title: "Dashboard & Sidebar Refinements",
    description: "UI improvements and sidebar navigation enhancements",
    details: [
      "Redesigned dashboard layout for better information hierarchy",
      "Fixed sidebar navigation bugs and improved state management",
      "Added smooth transitions and animations to sidebar interactions",
      "Improved mobile sidebar functionality with better touch targets",
      "Enhanced dashboard statistics display and filtering options",
    ],
    category: "enhancement",
    commit: "3519f2e",
  },
  {
    date: "October 25, 2025",
    version: "2.6.0",
    title: "New Pricing Plans & Access Gates",
    description: "Introduced tiered pricing with feature access controls",
    details: [
      "Implemented three-tier pricing model: Starter, Professional, Enterprise",
      "Added feature gates to restrict advanced functionality based on plan level",
      "Created pricing calculator for accurate usage-based billing",
      "Added plan upgrade/downgrade flow with immediate feature activation",
      "Integrated pricing limits with usage tracking and notifications",
    ],
    category: "feature",
    commit: "333519e",
  },
  {
    date: "October 22, 2025",
    version: "2.5.1",
    title: "Landing Page Redesign",
    description: "Complete visual overhaul of marketing landing page",
    details: [
      "Redesigned hero section with improved copy and CTA placement",
      "Enhanced feature showcase with better visual hierarchy",
      "Added social proof section with testimonials and case studies",
      "Improved responsive design across all device sizes",
      "Optimized page performance and SEO",
    ],
    category: "enhancement",
    commit: "685c24c",
  },
  {
    date: "October 20, 2025",
    version: "2.4.5",
    title: "Pricing Strategy Update",
    description: "Refined pricing model and removed legacy usage guards",
    details: [
      "Simplified pricing tiers based on recruiter feedback",
      "Removed outdated usage guard restrictions",
      "Improved transparency in feature availability",
      "Added detailed pricing documentation",
      "Created migration path for existing customers",
    ],
    category: "enhancement",
    commit: "4fa85bc",
  },
  {
    date: "October 18, 2025",
    version: "2.4.0",
    title: "Sidebar Navigation Overhaul",
    description: "Complete redesign of sidebar with improved UX",
    details: [
      "Implemented collapsible sidebar with minimized icon-only view",
      "Added persistent sidebar state in localStorage",
      "Improved navigation icons and labels",
      "Enhanced keyboard navigation and accessibility",
      "Added tooltips for minimized navigation items",
    ],
    category: "enhancement",
    commit: "7ec43b0",
  },
  {
    date: "October 15, 2025",
    version: "2.3.0",
    title: "Dashboard Summary Component",
    description: "New executive summary component for quick insights",
    details: [
      "Created dashboard summary widget showing key metrics at a glance",
      "Added configurable metric selection and custom views",
      "Implemented real-time data updates for summary metrics",
      "Added export functionality for summary reports",
      "Enhanced visual representation with charts and graphs",
    ],
    category: "feature",
    commit: "ff7fcf5",
  },
  {
    date: "October 12, 2025",
    version: "2.2.0",
    title: "Recruiter Interaction Tracking",
    description: "Track and analyze recruiter engagement patterns",
    details: [
      "Implemented recruiter interaction capture system",
      "Added feedback collection for candidate profiles and interviews",
      "Created analytics dashboard for recruiter activity tracking",
      "Added performance metrics based on recruiter actions",
      "Integrated with admin dashboard for compliance and oversight",
    ],
    category: "feature",
    commit: "984b293",
  },
  {
    date: "October 9, 2025",
    version: "2.1.5",
    title: "Spotlight Interview Videos",
    description: "Enhanced candidate profile with intro video showcase",
    details: [
      "Added spotlight video display on candidate profile pages",
      "Implemented auto-play with mute option for better UX",
      "Added video transcript and key highlights",
      "Created admin tools to mark videos as spotlight content",
      "Improved video player controls and accessibility",
    ],
    category: "enhancement",
    commit: "3b77b2c",
  },
];

const ChangelogEntry = ({ entry }: { entry: ChangelogEntry }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const categoryColor = {
    feature: "border border-[#d4a853] bg-[#f8f3e8] text-[#7b5804]",
    fix: "border border-[#d8cfbf] bg-[#efebe3] text-[#4e4637]",
    enhancement: "border border-[#e7dcc4] bg-white text-[#6b5d43]",
  };

  const categoryLabel = {
    feature: "Feature",
    fix: "Fix",
    enhancement: "Enhancement",
  };

  return (
    <motion.div
      className="relative pl-8 pb-12"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      viewport={{ once: true }}
    >
      <div className="absolute left-0 top-2 h-full w-px bg-[#e7dcc4]" />
      <div className="absolute -left-1 top-2 h-3 w-3 bg-[#d4a853]" />

      <div className="space-y-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-[#7d7465]">
              {entry.date} • v{entry.version}
            </p>
            <h3 className="mt-1 font-display text-xl font-semibold text-[#1b1c19] sm:text-2xl">
              {entry.title}
            </h3>
          </div>
          <div className="flex items-center gap-3">
            <span className={`px-3 py-1 text-xs font-semibold ${categoryColor[entry.category]}`}>
              {categoryLabel[entry.category]}
            </span>
            <span className="rounded bg-[#f3eee4] px-2 py-1 text-xs font-mono text-[#7d7465]">
              {entry.commit}
            </span>
          </div>
        </div>

        <p className="text-sm leading-relaxed text-[#4e4637] sm:text-base">{entry.description}</p>

        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="mt-2 flex items-center gap-2 text-sm font-semibold text-[#7b5804] hover:text-[#5f4303]"
        >
          {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          {isExpanded ? "Hide details" : "Show details"}
        </button>

        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: isExpanded ? 1 : 0, height: isExpanded ? "auto" : 0 }}
          transition={{ duration: 0.25 }}
          className="overflow-hidden"
        >
          <ul className="mt-3 space-y-2 text-sm text-[#4e4637]">
            {entry.details.map((detail, idx) => (
              <li key={idx} className="flex gap-3">
                <span className="mt-0.5 font-bold text-[#d4a853]">•</span>
                <span>{detail}</span>
              </li>
            ))}
          </ul>
        </motion.div>
      </div>
    </motion.div>
  );
};

const ChangelogPage = () => {
  const [filter, setFilter] = useState<"all" | "feature" | "fix" | "enhancement">("all");
  const filteredEntries =
    filter === "all" ? changelogData : changelogData.filter((entry) => entry.category === filter);
  const latest = changelogData[0];
  const totalReleases = changelogData.length;

  return (
    <div className="technical-home relative min-h-screen antialiased overflow-x-hidden">
      <section className="relative pb-16 pt-28">
        <div className="container mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-5xl"
          >
            <span className="inline-flex items-center gap-2 border px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.22em]">
              Product Changelog
            </span>
            <h1 className="mt-8 font-display text-5xl font-bold leading-[0.95] tracking-tight text-[#1b1c19] md:text-7xl">
              Every release, in one place.
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-[#4e4637] sm:text-xl">
              Follow the product’s evolution across features, enhancements, and fixes as we turn
              recruiting workflows into an intelligence-first experience.
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-[#4e4637]">
              <div className="inline-flex items-center gap-2 border border-[#e7dcc4] bg-white px-3 py-1.5">
                <span className="h-2 w-2 bg-[#d4a853]" />
                <span>
                  Latest: <span className="font-semibold">v{latest.version}</span>{" "}
                  <span className="text-[#7d7465]">({latest.date})</span>
                </span>
              </div>
              <span className="text-[#7d7465]">
                {totalReleases} logged updates, since early 2025
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto flex flex-col gap-10 px-6 lg:flex-row lg:px-12">
          <aside className="lg:w-72 flex-shrink-0 space-y-6">
            <div className="border border-[#e7dcc4] bg-white p-5">
              <h2 className="mb-2 font-display text-base font-semibold tracking-wide text-[#1b1c19]">
                Filter updates
              </h2>
              <p className="mb-4 text-xs text-[#7d7465]">
                Focus on the changes that matter most to your workflow.
              </p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { id: "all", label: "All" },
                  { id: "feature", label: "Features" },
                  { id: "enhancement", label: "Enhancements" },
                  { id: "fix", label: "Fixes" },
                ].map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setFilter(item.id as "all" | "feature" | "enhancement" | "fix")}
                    className={`border px-3 py-1.5 text-xs font-medium transition-colors ${
                      filter === item.id
                        ? "border-[#d4a853] bg-[#f8f3e8] text-[#7b5804]"
                        : "border-[#e7dcc4] bg-white text-[#4e4637] hover:border-[#d4a853] hover:text-[#7b5804]"
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="border border-[#e7dcc4] bg-[#f8f3e8] p-5">
              <h3 className="mb-2 font-display text-base font-semibold text-[#1b1c19]">
                Release cadence
              </h3>
              <p className="text-xs text-[#7d7465]">
                We ship improvements multiple times a week, from small UX refinements to entirely
                new workflows for recruiters.
              </p>
            </div>
          </aside>

          <div className="flex-1">
            <div className="space-y-10">
              {filteredEntries.map((entry, idx) => (
                <ChangelogEntry key={idx} entry={entry} />
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="technical-dark-section relative bg-[#1b1c19] py-20">
        <div className="container mx-auto px-6 text-center lg:px-12">
          <h2 className="mx-auto max-w-3xl font-display text-3xl md:text-5xl">
            Ready to transform your hiring?
          </h2>
          <p className="mx-auto mb-8 mt-5 max-w-2xl text-lg">
            Experience the power of AI-driven candidate discovery and engagement.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/pricing"
              className="border border-[#d4a853] bg-[#d4a853] px-8 py-3 font-semibold text-[#1b1c19] transition-colors hover:bg-[#e4bc6a]"
            >
              Start Free Trial
            </Link>
            <a
              href="mailto:apex25@theresume.io?subject=Demo Request"
              className="border border-[rgba(212,168,83,0.35)] bg-transparent px-8 py-3 font-semibold text-[rgba(250,249,244,0.88)] transition-colors hover:border-[#d4a853] hover:text-[#f0d08a]"
            >
              Schedule Demo
            </a>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
};

export default ChangelogPage;
